import { isValidPhoneNumber } from "react-phone-number-input";
import { fileSchema } from "../files/files.schema";
import { z } from "zod";
import {
  sirenSchema,
  siretSchema,
  vatSchema,
  validateVatWithSiren,
} from "..//utils";
import { colorValues } from "@ac-expo/api-types";

export const invalidUrl = "URL invalide";
export const invalidPlatformUrl = (platform: string) =>
  `L'URL doit être valide pour ${platform}`;
export const validatePlatformUrl = (platformDomains: string[]) => {
  return (value: string | null | undefined) => {
    if (!value) return true;
    try {
      const url = new URL(value);

      return platformDomains.some((domain) => url.hostname.includes(domain));
    } catch {
      return false;
    }
  };
};

export const updateStructureSchema = z
  .object({
    picture: fileSchema.nullable(),
    bannerPicture: fileSchema.nullable(),
    businessName: z.string().nullish(),
    businessSector: z.string().nullish(),
    description: z.string().max(200, "La description ne peut pas dépasser 200 caractères").nullish(),
    businessEmail: z.string().email("Email invalide").nullish(),
    phoneNumber: z
      .string()
      .refine((value) => isValidPhoneNumber(value, "FR"))
      .or(z.literal(""))
      .transform((value) => (value === "" ? null : value))
      .nullish(),
    address: z
      .object({
        name: z.string().nullish(),
        address: z.string().nullish(),
        lat: z.number().optional().nullable(),
        long: z.number().optional().nullable(),
        zipCode: z.string().nullish(),
        city: z.string().nullish(),
        country: z.string().nullish(),
      })
      .nullish(),
    siret: siretSchema
      .or(z.literal(""))
      .nullish()
      .transform((value) => (value === "" ? null : value)),
    siren: sirenSchema
      .or(z.literal(""))
      .nullish()
      .transform((value) => (value === "" ? null : value)),
    apeCode: z
      .union([
        z
          .string()
          .length(5)
          .refine((value) => /^[0-9]{4}[A-Z]$/.test(value)),
        z
          .string()
          .length(6)
          .refine((value) => /^[0-9]{2}\.[0-9]{2}[A-Z]$/.test(value)),
      ])
      .or(z.literal(""))
      .nullish()
      .transform((value) => (value === "" ? null : value)),
    vat: vatSchema
      .or(z.literal(""))
      .nullish()
      .transform((value) => (value === "" ? null : value)),
    socials: z
      .object({
        websiteUrl: z.string().url(invalidUrl).or(z.literal("")).nullish(),
        linkedinUrl: z
          .string()
          .url(invalidUrl)
          .refine(validatePlatformUrl(["linkedin.com"]), {
            message: invalidPlatformUrl("LinkedIn"),
          })
          .or(z.literal(""))
          .nullish(),
        facebookUrl: z
          .string()
          .url(invalidUrl)
          .refine(validatePlatformUrl(["facebook.com"]), {
            message: invalidPlatformUrl("Facebook"),
          })
          .or(z.literal(""))
          .nullish(),
        twitterUrl: z
          .string()
          .url(invalidUrl)
          .refine(validatePlatformUrl(["twitter.com", "x.com"]), {
            message: invalidPlatformUrl("X (Twitter)"),
          })
          .or(z.literal(""))
          .nullish(),
        instagramUrl: z
          .string()
          .url(invalidUrl)
          .refine(validatePlatformUrl(["instagram.com"]), {
            message: invalidPlatformUrl("Instagram"),
          })
          .or(z.literal(""))
          .nullish(),
        youtubeUrl: z
          .string()
          .url(invalidUrl)
          .refine(validatePlatformUrl(["youtube.com"]), {
            message: invalidPlatformUrl("YouTube"),
          })
          .or(z.literal(""))
          .nullish(),
      })
      .nullish(),
  })
  .superRefine((values, ctx) => {
    const siretOrSiren = values.siret || values.siren;
    validateVatWithSiren(ctx, values.vat, siretOrSiren);
  });

export const updateCustomStructureAppearanceSchema = z.object({
  backgroundPicture: fileSchema.nullable(),
  filterOpacity: z.number().min(0).max(100),
  backgroundColor: z.enum(colorValues),
});

export const defaultUpdateStructureValues = {
  picture: null,
  businessName: null,
  businessSector: null,
  description: null,
  businessEmail: null,
  phoneNumber: null,
  address: {
    name: null,
    address: null,
    lat: null,
    long: null,
    zipCode: null,
    city: null,
    country: null,
  },
  siret: null,
  siren: null,
  apeCode: null,
  vat: null,
  socials: {
    websiteUrl: null,
    linkedinUrl: null,
    facebookUrl: null,
    twitterUrl: null,
    instagramUrl: null,
    youtubeUrl: null,
  },
};

export const defaultUpdateCustomStructureAppearanceValues = {
  backgroundPicture: null,
  backgroundColor: "white" as const,
  filterOpacity: 0,
};

export type UpdateStructureForm = z.infer<typeof updateStructureSchema>;

export type UpdateCustomStructureAppearanceForm = z.infer<
  typeof updateCustomStructureAppearanceSchema
>;
